import { Block } from "@components/Block/Block";
import { JWMHtml } from "@components/JWMHtml/JWMHtml";
import { JWMTabs } from "@components/JWMTabs/JWMTabs";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import analyticstracker from "@jmc/analyticstracker";
import { TabImpression } from "@jmc/core/src/components/TabImpression/index";
import { EventTypes } from "@jmc/core/src/types/EventTypes";
import { Tabs } from "@jmc/solid-design-system/src/components/molecules/Tabs/Tabs";
import { BreakPoint, useMediaQuery } from "@jmc/solid-design-system/src/hooks/useMediaQuery/useMediaQuery";
import cleanCSSIdentifier from "@jmc/utils/utils/clean-css-identifier";
import { CMSBlock } from "@jwm/types/CMSBlock";
import { CMSTabPane } from "@jwm/types/CMSTabsProps";
import { ApplicationState } from "@redux/modules/index";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import style from "../style.module.scss";
import { trackingHelpersProps } from "../types";

interface TabsBlockProps {
    content: CMSBlock["tabs"];
    placement?: string;
    locale?: string;
    trackingHelpers: trackingHelpersProps;
    sectionBackground?: string;
    sectionIndex?: number;
    siteSpecialty?: {
        type: string;
        title: string;
    };
}

export const TabsBlock = ({
    content,
    placement,
    locale,
    trackingHelpers,
    sectionBackground,
    sectionIndex,
    siteSpecialty,
}: TabsBlockProps): JSX.Element => {
    const [activeIndex, setActiveIndex] = useState(null);
    const [initialLoad, setInitialLoad] = useState(true);

    const paneId = useSelector((state: ApplicationState) => state.panes.tabPaneId);
    const isMobile = useMediaQuery(BreakPoint.sm);

    useEffect(() => {
        if (typeof window !== `undefined` && window?.location?.hash && initialLoad) {
            loadActiveTab();
        }
        if (activeIndex) setInitialLoad(false);
    });

    useEffect(() => {
        window.addEventListener("hashchange", onHashChange);

        return (): void => {
            window.removeEventListener("hashchange", onHashChange);
        };
    });

    // Verify if paneId is available for referencing footnotes
    useEffect(() => {
        if (typeof window !== `undefined` && window?.location?.hash && paneId) {
            onHashChange();
        }
    }, [paneId]);

    const filteredTab = (paneTitle: string): number => {
        const filteredTab = content?.panes?.findIndex((pane) => {
            return cleanCSSIdentifier(`Tab.${pane?.pane?.title?.toLowerCase()}`) === paneTitle;
        });
        return filteredTab;
    };

    const loadActiveTab = (): void => {
        if (typeof window !== `undefined`) {
            const hash = decodeURI(window?.location?.hash?.replace("#", ""));
            if (filteredTab(hash) > -1) {
                const currentTabElement = document?.querySelector(`[data-target-tabname="${hash}"]`);
                const currentIndex = currentTabElement?.getAttribute("data-tracking-index");
                setActiveIndex(parseInt(currentIndex));
            } else {
                const closestTabElement = document?.getElementById(hash)?.closest("[data-target-tab]");
                const closestTabName = closestTabElement?.getAttribute("data-target-tab");
                const closestTabId = closestTabElement?.getAttribute("id");
                const activeTabId = `${placement}-tabpane.${cleanCSSIdentifier(closestTabName?.toLowerCase())}`;
                const filteredIndex = filteredTab(`Tab.${closestTabName}`);
                if (filteredIndex > -1 && closestTabId === activeTabId) {
                    setActiveIndex(filteredIndex);
                }
            }
        }
    };

    const onHashChange = (): void => {
        loadActiveTab();
    };

    return (
        <JWMTabs
            selectedTab={activeIndex}
            onChange={(index: number, e: React.MouseEvent) => {
                setActiveIndex(index);
                analyticstracker().trackInteraction(e, {
                    changeEvent: EventTypes.TAB_CLICK,
                    addData: {
                        info: {
                            name: content.panes[index].pane.title,
                            position: index + 1,
                            placement,
                            state: index === 0 ? "auto-visible" : "click-visible",
                        },
                    },
                });
            }}
            sectionBackground={sectionBackground}
        >
            {content?.panes?.map(({ pane }: CMSTabPane, index: number) => {
                trackingHelpers.numTabs++;
                const title = (
                    <JWMHtml placement={placement} siteSpecialty={siteSpecialty} showGlossary={false}>
                        {pane?.title}
                    </JWMHtml>
                );

                const rawTitle = title.props?.children;

                return (
                    <Tabs.Tab
                        key={index}
                        title={title}
                        rawTitle={rawTitle}
                        autoId={isMobile ? cleanCSSIdentifier(`Tab.${rawTitle?.toLowerCase()}`) : null}
                    >
                        <div
                            aria-labelledby={`tabs-${cleanCSSIdentifier(rawTitle?.toLowerCase())}`}
                            className={style.tabContent}
                            id={`${placement}-tabpane.${cleanCSSIdentifier(rawTitle.toLowerCase())}`}
                            role="tabpanel"
                            data-target-tab={`${cleanCSSIdentifier(rawTitle?.toLowerCase())}`}
                            data-target-tabname={cleanCSSIdentifier(`Tab.${rawTitle?.toLowerCase()}`)}
                            data-tracking-index={index}
                        >
                            {pane?.content?.map((block: CMSBlock, i: number) => {
                                const paneBlock = (): JSX.Element => {
                                    return (
                                        <Block
                                            key={i}
                                            content={block}
                                            placement={`${placement}:tab-${trackingHelpers.numTabs}`}
                                            locale={locale}
                                            sectionBackground={sectionBackground}
                                            sectionIndex={sectionIndex}
                                            siteSpecialty={siteSpecialty}
                                        />
                                    );
                                };
                                return (
                                    <TabImpression.Content
                                        placement={placement}
                                        key={`${pane.title}-${i}`}
                                        position={index + 1}
                                        name={pane.title}
                                        open={index === 0}
                                    >
                                        {block?.player ? (
                                            <div key={i} className={style.tabContent__videoPlayer}>
                                                {paneBlock()}
                                            </div>
                                        ) : (
                                            paneBlock()
                                        )}
                                    </TabImpression.Content>
                                );
                            })}
                        </div>
                    </Tabs.Tab>
                );
            })}
        </JWMTabs>
    );
};
